/**
 * asaasCharge — a COBRANÇA Pix de um documento. MÓDULO PURO.
 *
 * `paymentDetection.ts` responde "esta empresa cobra pelo Asaas?"; aqui a
 * pergunta é "esta cobrança específica ainda pode ser paga, e é ela que vai no
 * documento?".
 *
 * ─── A REGRA QUE EVITA O PIOR ERRO POSSÍVEL ─────────────────────────────────
 *
 * Um QR só vai ao papel enquanto a cobrança está **em aberto**. Imprimir o QR de
 * uma cobrança já paga faria o cliente **pagar duas vezes** — e Pix não estorna
 * sozinho: o prestador teria de devolver na mão, para um cliente que fez tudo
 * certo. É o modo de falha mais caro desta feature inteira, e ele não dá erro
 * nenhum: o QR continua tecnicamente válido depois de pago.
 *
 * Por isso `pending`/`overdue` são os ÚNICOS status pagáveis. `confirmed` já é
 * pagamento (só falta liquidar), e `received`/`received_in_cash` idem;
 * `refunded`/`chargeback`/`cancelled` não valem mais nada.
 *
 * ─── POR QUE A COBRANÇA VENCE O INTERRUPTOR ─────────────────────────────────
 *
 * O interruptor da empresa decide se uma cobrança **nasce**; a existência dela é
 * que decide o que o documento **imprime**. Os dois papéis são distintos, e
 * confundi-los produz a pior divergência possível: a página pública `/o/:id` não
 * tem sessão e não consegue ler `payment_settings` nem `asaas_accounts` — ela só
 * enxerga a cobrança (via `get_public_quote`). Se o app decidisse pelo
 * interruptor e a página pela cobrança, o PDF do prestador e a tela do cliente
 * discordariam sobre como pagar, exatamente no documento em que isso não pode
 * acontecer.
 *
 * Consequência deliberada: desligar o interruptor **não** desfaz uma cobrança já
 * emitida. É o certo — ela já foi enviada ao cliente, continua pagável e
 * continua sendo conciliada pelo webhook. O interruptor vale para as próximas.
 */

/** Espelha o enum `public.asaas_charge_status`. */
export type AsaasChargeStatus =
  | 'pending'
  | 'confirmed'
  | 'received'
  | 'received_in_cash'
  | 'overdue'
  | 'refunded'
  | 'chargeback'
  | 'cancelled';

export interface AsaasChargeLike {
  id?: string;
  status?: string | null;
  value?: number | string | null;
  pix_payload?: string | null;
  pix_qr_base64?: string | null;
  invoice_url?: string | null;
  environment?: string | null;
  created_at?: string | null;
}

/**
 * Os únicos status em que o QR ainda deve ir ao documento. Ver o cabeçalho —
 * acrescentar `confirmed` aqui é a regressão plausível ("ainda não liquidou,
 * então não pagou"), e ela faz o cliente pagar de novo.
 */
export const PAYABLE_CHARGE_STATUSES: readonly AsaasChargeStatus[] = ['pending', 'overdue'];

const PAYABLE = new Set<string>(PAYABLE_CHARGE_STATUSES);

/** Status desconhecido NUNCA é pagável: na dúvida, não imprima o QR. */
export function isChargePayable(status: unknown): boolean {
  return typeof status === 'string' && PAYABLE.has(status);
}

/**
 * Status em que o cliente JÁ PAGOU (mesmo que o dinheiro não tenha liquidado).
 *
 * Eles não só deixam de imprimir o QR da cobrança: eles **proíbem o documento de
 * cair no QR estático**. Sem isso, um orçamento quitado volta a mostrar a chave
 * pessoal assim que o modo resolve como estático — o que acontece com o
 * interruptor desligado depois da venda e, pior, sempre que o app está OFFLINE.
 * Medido em device (2026-08-02).
 */
export const PAID_CHARGE_STATUSES: readonly AsaasChargeStatus[] = [
  'confirmed',
  'received',
  'received_in_cash',
];

const PAID = new Set<string>(PAID_CHARGE_STATUSES);

/** O cliente já pagou (mesmo que o dinheiro ainda não tenha liquidado). */
export function isChargePaid(status: unknown): boolean {
  return typeof status === 'string' && PAID.has(status);
}

/**
 * O que a página pública `/o/:id` precisa saber sobre a cobrança — pagável **ou
 * já paga**. As duas coisas mudam o documento: a primeira coloca o QR da
 * cobrança, a segunda TIRA qualquer QR. Devolver só as pagáveis faria a tela do
 * cliente que já pagou cair no QR estático e convidá-lo a pagar de novo.
 */
export const PUBLIC_CHARGE_STATUSES: readonly AsaasChargeStatus[] = [
  ...PAYABLE_CHARGE_STATUSES,
  ...PAID_CHARGE_STATUSES,
];

/**
 * Numérico a partir do que o PostgREST devolve — `numeric` vem como STRING com
 * ponto (`"250.50"`). `Number('')` é 0 e `Number(null)` é 0, e um valor zero
 * aqui casaria com um documento zerado; por isso a ausência devolve `null`
 * (#61: ausência não é zero).
 */
export function chargeValue(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * A cobrança serve para o cliente PAGAR pelo documento?
 *
 * Em aberto **e com o código**. As duas metades importam, e a segunda é a que
 * escapou no primeiro E2E: o Asaas pode criar a cobrança e FALHAR ao gerar o QR
 * (aconteceu — `invalid_action` na chamada do `pixQrCode`). Aí existe cobrança
 * `pending` e não existe nada para imprimir: o documento sai sem QR e a tela,
 * olhando só o status, anunciava "o orçamento já sai com o QR" e oferecia
 * "Copiar código" com o código vazio. Tela e documento discordando é o defeito
 * que esta feature inteira existe para não cometer (#9).
 */
export function chargeIsUsable(charge: AsaasChargeLike | null | undefined): boolean {
  return (
    isChargePayable(charge?.status) &&
    typeof charge?.pix_payload === 'string' &&
    charge.pix_payload.trim().length > 0 &&
    chargeValue(charge?.value) !== null
  );
}

/**
 * A cobrança que vai ao documento, entre as que existirem — ou `null`.
 *
 * Um orçamento pode acumular várias: a primeira venceu, outra foi cancelada, o
 * prestador reemitiu. Só uma pode ser impressa, e a escolha errada manda o
 * cliente pagar uma cobrança morta.
 *
 * Ordem: só as PAGÁVEIS e com payload, produção antes de sandbox (uma cobrança
 * de teste jamais pode aparecer num documento real), e a mais RECENTE entre elas
 * — a reemissão é o que o prestador acabou de decidir.
 */
export function pickPayableCharge<T extends AsaasChargeLike>(
  charges: readonly T[] | null | undefined,
): T | null {
  if (!Array.isArray(charges)) return null;

  const usable = charges.filter(chargeIsUsable);
  if (usable.length === 0) return null;

  const production = usable.filter((charge) => charge.environment === 'production');
  const pool = production.length > 0 ? production : usable;

  return [...pool].sort((a, b) => {
    const at = Date.parse(a.created_at ?? '') || 0;
    const bt = Date.parse(b.created_at ?? '') || 0;
    return bt - at;
  })[0];
}

/**
 * A cobrança MAIS RELEVANTE para a TELA — que não é a mesma pergunta do
 * documento. O papel mostra só o que dá para pagar; a tela precisa mostrar
 * também a que já foi paga (é a boa notícia) e a que falhou (é o que o prestador
 * tem de resolver). Uma função só para as duas faria uma das telas mentir.
 */
export function pickCurrentCharge<T extends AsaasChargeLike>(
  charges: readonly T[] | null | undefined,
): T | null {
  if (!Array.isArray(charges) || charges.length === 0) return null;
  const rank = (charge: T): number => {
    if (isChargePaid(charge.status)) return 0;
    if (isChargePayable(charge.status)) return 1;
    return 2;
  };
  return [...charges].sort((a, b) => {
    const byRank = rank(a) - rank(b);
    if (byRank !== 0) return byRank;
    return (Date.parse(b.created_at ?? '') || 0) - (Date.parse(a.created_at ?? '') || 0);
  })[0];
}

/**
 * De quanto em quanto tempo reperguntar ao servidor enquanto o cliente não pagou.
 *
 * ─── POR QUE EXISTE (não é preferência de UX, é a promessa do produto) ──────
 *
 * Quem muda a linha é o WEBHOOK, no servidor — o app não participa do pagamento
 * e portanto não tem como saber que ele aconteceu. Sem reperguntar, o card fica
 * em "Aguardando" com o dinheiro JÁ na conta do prestador, e só uma remontagem
 * da tela (o `refetchOnMount: 'always'`) o corrige. Medido em teste manual:
 * a notificação de push chegava e o card não mexia.
 *
 * O caminho instantâneo seria o Realtime do Supabase — que neste projeto
 * publica SÓ `profiles`: as assinaturas de `notifications` e `nfse_documents`
 * são no-op silencioso. Perguntar de tempos em tempos funciona com a infra de
 * hoje, sobrevive a socket caído e é verificável no aparelho.
 */
export const OPEN_CHARGE_POLL_MS = 8_000;

/**
 * Vale reperguntar? Só enquanto existe cobrança EM ABERTO na tela.
 *
 * Paga, cancelada ou inexistente: nada mais vai mudar sozinho, e continuar
 * perguntando seria consumo puro. É por isso que a decisão mora aqui e não num
 * `setInterval` da tela — ela é a mesma regra de negócio do resto do módulo, e
 * precisa de teste.
 */
export function shouldPollCharges(charges: readonly AsaasChargeLike[] | null | undefined): boolean {
  const current = pickCurrentCharge(charges ?? []);
  return Boolean(current && isChargePayable(current.status));
}

/**
 * A cobrança no formato que o gerador de PDF consome (`PdfPixDynamic`).
 *
 * Ponto ÚNICO de conversão, chamado pelos 3 call-sites que montam
 * `PDFQuoteData` (tela do orçamento, tela de criação e página pública). Com a
 * conversão repetida, um deles esqueceria `status` — e o guard do PDF, que
 * recusa cobrança já paga, receberia `undefined` e simplesmente não imprimiria
 * QR nenhum. Falha para o lado seguro, mas silenciosa.
 *
 * `null` (sem cobrança) atravessa como `null`: é o que faz o documento cair no
 * QR estático ou, no modo dinâmico, sair sem QR.
 */
export function toPdfPixDynamic(charge: AsaasChargeLike | null | undefined): {
  payload: string;
  qrBase64?: string | null;
  value: number;
  status: string;
} | null {
  if (!charge) return null;
  const value = chargeValue(charge.value);
  const payload = typeof charge.pix_payload === 'string' ? charge.pix_payload.trim() : '';
  if (!payload || value === null) return null;
  return {
    payload,
    qrBase64: charge.pix_qr_base64 ?? null,
    value,
    // Sem `?? ''`: um status ausente tem de chegar como string vazia e ser
    // recusado por `isChargePayable`, não virar um valor que passe no guard.
    status: typeof charge.status === 'string' ? charge.status : '',
  };
}

/** Rótulo curto para o selo da tela. Curto de propósito (#52). */
export const CHARGE_STATUS_LABELS: Record<AsaasChargeStatus, string> = {
  pending: 'Aguardando',
  overdue: 'Vencida',
  confirmed: 'Pago',
  received: 'Recebido',
  received_in_cash: 'Pago em dinheiro',
  refunded: 'Estornado',
  chargeback: 'Contestado',
  cancelled: 'Cancelada',
};

export function chargeStatusLabel(status: unknown): string {
  return typeof status === 'string' && status in CHARGE_STATUS_LABELS
    ? CHARGE_STATUS_LABELS[status as AsaasChargeStatus]
    : 'Desconhecido';
}
